/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    type ReactElement,
    type ReactNode,
    type PropsWithChildren,
    useRef,
    useEffect,
    useCallback,
    useState,
} from 'react';
import { useLocation } from 'react-router';
import { Link } from '@/components/link';
import Search from './search';
import CartBadge from './cart-badge';
import UserActions from './user-actions/user-actions';
import { useTranslation } from 'react-i18next';
import logo from '/images/mahindraHeader.png';
import { Button } from '@/components/ui/button';
import { SparklesIcon, HeaderSearchIcon } from '@/components/icons';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { launchChat } from '@/components/shopper-agent';
import { validateShopperAgentConfig } from '@/components/shopper-agent/shopper-agent.utils';
import { UITarget } from '@/targets/ui-target';
import WishlistIcon from './wishlist-icon';

interface HeaderProps extends PropsWithChildren {
    beforeHeader?: ReactNode;
    variant?: 'full' | 'checkout' | 'logoOnly';
}

// Isolates the `useLocation()` subscription so that route changes only re-render the Search component (via key reset)
// without cascading a re-render through the entire `Header` tree, which would unnecessarily re-render the navigation
// menu and other stable children.
function LocationKeyedSearch({
    layout,
    autoFocus,
    placeholder,
    onSearchComplete,
}: {
    layout?: 'desktop' | 'overlay';
    autoFocus?: boolean;
    placeholder?: string;
    onSearchComplete?: () => void;
}) {
    const location = useLocation();
    return (
        <Search
            key={`${location.pathname}${location.search}`}
            layout={layout}
            autoFocus={autoFocus}
            placeholder={placeholder}
            onSearchComplete={onSearchComplete}
        />
    );
}

export default function Header({ children, beforeHeader, variant = 'full' }: HeaderProps): ReactElement {
    const { t } = useTranslation('header');
    const headerRef = useRef<HTMLElement>(null);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [categoriesHovered, setCategoriesHovered] = useState(false);
    const config = useConfig();
    const showChat =
        variant === 'full' &&
        (config.commerceAgent?.enabled === 'true' || config.commerceAgent?.enabled === true) &&
        validateShopperAgentConfig(config.commerceAgent);
    const updateHeaderHeight = useCallback(() => {
        if (headerRef.current) {
            const height = `${headerRef.current.offsetHeight}px`;
            headerRef.current.style.setProperty('--header-height', height);
            document.documentElement.style.setProperty('--header-height', height);
        }
    }, []);

    useEffect(() => {
        const el = headerRef.current;
        if (!el) return;
        updateHeaderHeight();
        const observer = new ResizeObserver(updateHeaderHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateHeaderHeight]);

    if (variant === 'logoOnly') {
        return (
            <header ref={headerRef} className="bg-header-background text-header-foreground sticky top-0 z-50">
                <div className="section-container py-4 md:py-6">
                    <div className="flex items-center">
                        <Link to="/" className="flex-shrink-0 flex items-center" data-testid="header-logo">
                            <img
                                src={logo}
                                alt={t('logoAlt')}
                                className="h-4 lg:h-4 w-auto [filter:var(--header-logo-filter)]"
                            />
                        </Link>
                    </div>
                </div>
            </header>
        );
    }

    if (variant === 'checkout') {
        return (
            <header ref={headerRef} className="bg-header-background text-header-foreground sticky top-0 z-50">
                <div className="section-container py-4 md:py-6 md:pr-6 md:pl-16 md:max-w-none">
                    <div className="header-inner-row flex items-center gap-x-1 lg:gap-x-6">
                        <Link to="/" className="flex-shrink-0 flex items-center" data-testid="header-logo">
                            <img
                                src={logo}
                                alt={t('logoAlt')}
                                className="h-4 lg:h-4 w-auto [filter:var(--header-logo-filter)]"
                            />
                        </Link>

                        {children && <div className="hidden lg:flex items-center">{children}</div>}

                        <div className="flex-1" />

                        {/* Search - desktop only */}
                        <div className="hidden lg:block" data-testid="header-search-desktop">
                            <LocationKeyedSearch />
                        </div>

                        {/* Icons group */}
                        <div className="header-action-icons flex items-center gap-2">
                            <Dialog open={mobileSearchOpen} onOpenChange={setMobileSearchOpen}>
                                <DialogTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="cursor-pointer p-0 text-header-foreground hover:bg-transparent hover:opacity-50 transition-opacity lg:hidden"
                                        aria-label={t('searchPlaceholder')}>
                                        <HeaderSearchIcon className="size-6" />
                                    </Button>
                                </DialogTrigger>
                                <DialogContent
                                    showCloseButton={false}
                                    className="left-0 top-0 flex h-dvh w-full max-w-none translate-x-0 translate-y-0 flex-col overflow-hidden rounded-none border-x-0 border-t-0 p-4 sm:max-w-none">
                                    <DialogHeader className="sr-only">
                                        <DialogTitle>{t('searchPlaceholder')}</DialogTitle>
                                        <DialogDescription>{t('searchPlaceholder')}</DialogDescription>
                                    </DialogHeader>
                                    <LocationKeyedSearch
                                        layout="overlay"
                                        autoFocus
                                        onSearchComplete={() => setMobileSearchOpen(false)}
                                    />
                                </DialogContent>
                            </Dialog>
                            <WishlistIcon />
                            <CartBadge interactive={false} />
                            <UserActions className={children ? 'hidden lg:flex' : 'flex'} />
                            {children && <div className="lg:hidden">{children}</div>}
                        </div>
                    </div>
                </div>
            </header>
        );
    }

    return (
        <header
            ref={headerRef}
            data-categories-hovered={categoriesHovered ? 'true' : undefined}
            className="bg-header-background text-header-foreground sticky top-0 z-50">
            <div className="flex justify-end section-container">{beforeHeader}</div>
            <div className="section-container pt-4 md:pt-6 pb-0 md:pb-0 md:pr-6 md:pl-16 md:max-w-none">
                {/* Top row: Logo left, Icons right */}
                <div className="header-inner-row flex items-center gap-x-0.5 lg:gap-x-6 pb-4 md:pb-5.5 lg:pb-5.5">
                    {/* Logo - color swapped by theme via --header-logo-filter in app.css */}
                    <Link to="/" className="flex-shrink-0 flex items-center" data-testid="header-logo">
                        <img
                            src={logo}
                            alt={t('logoAlt')}
                            className="h-4 lg:h-4 w-auto [filter:var(--header-logo-filter)]"
                        />
                    </Link>

                    {/* Navigation Menu - desktop only, next to logo */}
                    <div
                        className="header-nav-categories hidden lg:flex items-center"
                        onMouseEnter={() => setCategoriesHovered(true)}
                        onMouseLeave={() => setCategoriesHovered(false)}>
                        {children}
                    </div>

                    {/* Spacer - takes remaining space */}
                    <div className="flex-1" />

                    {/* Search - desktop only */}
                    <div className="hidden lg:block" data-testid="header-search-desktop">
                        <LocationKeyedSearch />
                    </div>

                    {/* Icons group - includes mobile hamburger */}
                    <div className="header-action-icons flex items-center gap-3 md:gap-6">
                        {/* <UITarget targetId="sfcc.header.before.cart" /> */}
                        {showChat && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="cursor-pointer  px-1 text-header-foreground hover:bg-transparent hover:opacity-50 transition-opacity"
                                onClick={() => launchChat()}
                                aria-label={t('openChat')}>
                                <SparklesIcon />
                            </Button>
                        )}
                        <Dialog open={mobileSearchOpen} onOpenChange={setMobileSearchOpen}>
                            <DialogTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="cursor-pointer p-0 text-header-foreground hover:bg-transparent hover:opacity-50 transition-opacity lg:hidden"
                                    aria-label={t('searchPlaceholder')}>
                                    <HeaderSearchIcon className="size-6" />
                                </Button>
                            </DialogTrigger>
                            <DialogContent
                                showCloseButton={false}
                                className="left-0 top-0 flex h-dvh w-full max-w-none translate-x-0 translate-y-0 flex-col overflow-hidden rounded-none border-x-0 border-t-0 p-4 sm:max-w-none">
                                <DialogHeader className="sr-only">
                                    <DialogTitle>{t('searchPlaceholder')}</DialogTitle>
                                    <DialogDescription>{t('searchPlaceholder')}</DialogDescription>
                                </DialogHeader>
                                <LocationKeyedSearch
                                    layout="overlay"
                                    autoFocus
                                    onSearchComplete={() => setMobileSearchOpen(false)}
                                />
                            </DialogContent>
                        </Dialog>
                        <WishlistIcon/>
                        <CartBadge />
                        <UserActions className="hidden lg:flex" />
                        <div className="lg:hidden">{children}</div>
                    </div>
                </div>

                <div className="lg:hidden" data-testid="header-search-mobile" />
                <UITarget targetId="sfcc.header.bnpl.banner" />
            </div>
        </header>
    );
}
